import type { RefundSortField } from '@/gql/graphql';
import { decimal, int, isoDate, oneOf, optionsOf, text, uuidList } from '@/hooks/useUrlState';

/** Keep the keys in sync with Go's payments refund statuses (the `status` filter takes these workflow strings). */
export type RefundStatusValue = 'pending' | 'verifying_card' | 'refunding' | 'refunded' | 'failure' | 'canceled';
export const REFUND_STATUS_LABELS: Record<RefundStatusValue, string> = {
  pending: 'Pending',
  verifying_card: 'Verifying card',
  refunding: 'Refunding',
  refunded: 'Refunded',
  failure: 'Failed',
  canceled: 'Canceled',
};

export type RefundMethodValue = 'pin' | 'cash' | 'tab';
export const REFUND_METHOD_LABELS: Record<RefundMethodValue, string> = { pin: 'PIN', cash: 'Cash', tab: 'Tab' };

export const REFUND_STATUS_OPTIONS = optionsOf(REFUND_STATUS_LABELS, 'Any status');
export const REFUND_METHOD_OPTIONS = optionsOf(REFUND_METHOD_LABELS, 'Any method');

export type RefundsSortColumn =
  | 'id'
  | 'status'
  | 'method'
  | 'amount'
  | 'payment'
  | 'order'
  | 'tab'
  | 'terminal'
  | 'recognitionDate'
  | 'createdAt';

/** DataGrid column `id` → GraphQL `RefundSortField` for server-side sort; its keys are the values `sort` accepts. */
export const REFUND_COLUMN_SORT: Record<RefundsSortColumn, RefundSortField> = {
  id: 'ID',
  status: 'STATUS',
  method: 'PAYMENT_METHOD',
  amount: 'AMOUNT',
  payment: 'PAYMENT_ID',
  order: 'ORDER_ID',
  tab: 'TAB_NAME',
  terminal: 'TERMINAL_NAME',
  recognitionDate: 'RECOGNITION_DATE',
  createdAt: 'CREATED_AT',
};

export const REFUNDS_DEFAULT_SORT: { sort: RefundsSortColumn; dir: 'asc' | 'desc' } = { sort: 'createdAt', dir: 'desc' };

/** The /refunds query string, in this order (see README "List pages, URL state"). */
export const refundsUrlSchema = {
  search: text(),
  status: oneOf(REFUND_STATUS_LABELS, ''),
  method: oneOf(REFUND_METHOD_LABELS, ''),
  amountMin: decimal(),
  amountMax: decimal(),
  from: isoDate(),
  to: isoDate(),
  terminals: uuidList(),
  sort: oneOf(REFUND_COLUMN_SORT, REFUNDS_DEFAULT_SORT.sort),
  dir: oneOf<'asc' | 'desc', 'asc' | 'desc'>({ asc: true, desc: true }, REFUNDS_DEFAULT_SORT.dir),
  page: int({ min: 1 }, 1),
  size: int({ min: 1, max: 500 }, 50),
};

/** The keys that live in the collapsible Filters panel: the panel opens on mount when any is set. */
export const REFUNDS_ADVANCED_KEYS = [
  'status',
  'method',
  'amountMin',
  'amountMax',
  'from',
  'to',
  'terminals',
] as const satisfies readonly (keyof typeof refundsUrlSchema)[];
